import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../college-theme.css';

const TABS = [
  { label: 'Overview', path: '/college' },
  { label: 'My List', path: '/college/list' },
  { label: 'Browse', path: '/college/browse' },
  { label: 'Visits', path: '/college-visits' },
  { label: 'Essays', path: '/college/essays' },
  { label: 'SAT Prep', path: '/sat-prep' },
  { label: 'Docs', path: '/college/docs' },
  { label: 'Access', path: '/college/access' },
  { label: 'Profile', path: '/college/profile' },
];

// Tabs that own a whole subtree — an Essay Detail page (/college/essays/:id)
// keeps Essays lit, a Browse Detail page keeps Browse lit.
const SUBTREE_TABS = ['/college/browse', '/college/essays'];

// Every first path segment under /college that belongs to a tab rather than to
// a school on the list, so /college/:id can be told apart from /college/docs.
const RESERVED_SEGMENTS = ['list', 'browse', 'docs', 'profile', 'essays', 'access'];

// A college Detail page (/college/:id) counts as "My List" being active.
function isTabActive(tab, pathname) {
  if (tab.path === '/college') return pathname === '/college';
  if (SUBTREE_TABS.includes(tab.path)) return pathname.startsWith(tab.path);
  if (tab.path === '/college/list') {
    if (pathname === '/college/list') return true;
    const segment = pathname.replace(/^\/college\//, '').split('/')[0];
    return pathname.startsWith('/college/') && segment && !RESERVED_SEGMENTS.includes(segment);
  }
  return pathname === tab.path;
}

export default function CollegeShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [gradYear, setGradYear] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/student-profile')
      .then((res) => { if (alive) setGradYear(res.data.profile?.gradYear || null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [api]);

  const todayShort = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <div className="college-scope" style={{ minHeight: '100vh' }}>
      <div style={{ borderBottom: '1px solid var(--college-ink)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '14px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Link to="/hub" style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--college-muted)', textDecoration: 'none' }}>
            ← Hub
          </Link>
          <span className="font-serif-editorial" style={{ fontSize: 22, letterSpacing: '0.02em' }}>The College Desk</span>
          <span className="font-mono-editorial" style={{ fontSize: 11, color: 'var(--college-muted)' }}>
            {gradYear ? `Class of ${gradYear} · ` : ''}{todayShort}
          </span>
        </div>
      </div>
      <div style={{ borderBottom: '1px solid var(--college-border)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 32px', display: 'flex', gap: 28, overflowX: 'auto' }}>
          {TABS.map((tab) => {
            const active = isTabActive(tab, location.pathname);
            return (
              <span
                key={tab.label}
                onClick={() => navigate(tab.path)}
                style={{
                  padding: '12px 2px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  color: active ? 'var(--college-ink)' : 'var(--college-muted)',
                  borderBottom: active ? '2px solid var(--college-ink)' : '2px solid transparent',
                }}
              >
                {tab.label}
              </span>
            );
          })}
        </div>
      </div>
      <Outlet />
    </div>
  );
}
